import { useCallback, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { CalendarClock, Plus, ReceiptText, Wallet } from 'lucide-react';
import { ApiError } from './api/client';
import { PeriodProvider } from './period-context';
import { LocaleProvider } from './i18n';
import { useSettings } from './api/hooks';
import { Envelopes } from './pages/Envelopes';
import { Envelope } from './pages/Envelope';
import { Feed } from './pages/Feed';
import { Obligations } from './pages/Obligations';
import { Settings } from './pages/Settings';
import { useSettingsButton } from './telegram';
import { haptic } from './telegram';
import { ToastProvider } from './components/Toast';
import { QuickExpenseProvider, useQuickExpense } from './quick-expense';
import { cn } from './lib/utils';
import { t } from './i18n';

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Мобильная сеть: агрессивный рефетч только вредит.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        retry: (failureCount, error) => {
          // 401/403 (чужой initData) и 404 повторять бессмысленно.
          if (error instanceof ApiError && error.statusCode >= 400 && error.statusCode < 500) {
            return false;
          }
          return failureCount < 2;
        },
      },
    },
  });
}

/**
 * Три раздела и отдельная кнопка «+»: трата — главное действие приложения, она
 * доступна с любого раздела одним касанием. Тап по конверту тоже открывает
 * трату, уже в этот конверт.
 */
const TABS = [
  { to: '/', Icon: Wallet, label: () => t('app.budget'), end: true },
  { to: '/feed', Icon: ReceiptText, label: () => t('app.feed'), end: false },
  { to: '/obligations', Icon: CalendarClock, label: () => t('app.payments'), end: false },
] as const;

/** Таб-бар только на трёх разделах: во вложенных экранах его место у «назад». */
function TabBar(): JSX.Element | null {
  const { pathname } = useLocation();
  const quick = useQuickExpense();
  if (!TABS.some((tab) => tab.to === pathname)) return null;

  const fade = {
    paddingBottom: 'calc(var(--nav-gap) + var(--safe-bottom))',
    // Контент под панелью мягко гаснет, а не обрезается её краем.
    background: 'linear-gradient(to top, var(--paper) 45%, transparent)',
  };
  const plus = (className: string, size = 'size-6'): JSX.Element => (
    <button
      type="button"
      aria-label={t('app.newExpense')}
      className={cn('pointer-events-auto flex shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform active:scale-90', className)}
      onClick={() => quick.open()}
    >
      <Plus className={size} strokeWidth={2.5} aria-hidden />
    </button>
  );

  // Таблетка с подложкой у активного раздела и отдельная круглая «+».
  return (
    <nav aria-label={t('app.sections')} className="pointer-events-none fixed inset-x-0 bottom-0 z-30 px-4 pt-6" style={fade}>
      <div className="mx-auto flex max-w-md items-center gap-2.5">
        <div className="pointer-events-auto flex h-[var(--nav-height)] flex-1 items-center gap-1 rounded-full border border-border px-[5px] shadow-lift backdrop-blur-2xl backdrop-saturate-150"
          style={{ backgroundColor: 'var(--tabbar-bg)' }}>
          {TABS.map(({ to, Icon, label, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => haptic.select()}
              className={({ isActive }) => cn('flex h-[calc(100%-10px)] flex-1 flex-col items-center justify-center gap-[2px] rounded-full text-[10.5px] font-semibold no-underline transition-colors duration-200', isActive ? 'bg-accent-soft text-accent' : 'text-faint')}>
              {({ isActive }) => (<><Icon className="size-[22px]" strokeWidth={isActive ? 2.2 : 1.8} aria-hidden />{label()}</>)}
            </NavLink>
          ))}
        </div>
        {plus('size-[var(--nav-height)] shadow-lift')}
      </div>
    </nav>
  );
}

/** «Настройки» дополнительно живут в меню клиента — но вход в шапке есть всегда. */
function SettingsBridge(): null {
  const navigate = useNavigate();
  const open = useCallback(() => navigate('/settings'), [navigate]);
  useSettingsButton(open);
  return null;
}

/** Язык интерфейса: из настроек, до их ответа: из профиля Telegram. */
function SettingsLocale({ children }: { children: React.ReactNode }): JSX.Element {
  const settings = useSettings();
  return <LocaleProvider language={settings.data?.language}>{children}</LocaleProvider>;
}

export function App(): JSX.Element {
  // QueryClient создаётся один раз на жизнь приложения.
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SettingsLocale>
        <PeriodProvider>
          <ToastProvider>
            <QuickExpenseProvider>
              <SettingsBridge />
              <div className="flex min-h-full flex-col">
                {/* Вертикальный отступ сверху задают шапки экранов: они сами
                    обходят вырез устройства. */}
                <main className="mx-auto w-full max-w-lg flex-1 pb-[calc(var(--nav-height)+var(--nav-gap)+var(--safe-bottom)+1.5rem)]">
                  <Routes>
                    <Route path="/" element={<Envelopes />} />
                    <Route path="/envelopes/:id" element={<Envelope />} />
                    <Route path="/feed" element={<Feed />} />
                    <Route path="/obligations" element={<Obligations />} />
                    <Route path="/settings" element={<Settings />} />
                    {/* Прежние адреса: ссылки из закладок и кнопки бота должны работать. */}
                    <Route path="/budget" element={<Navigate to="/" replace />} />
                    <Route path="/transactions" element={<Navigate to="/feed" replace />} />
                    <Route path="/history" element={<Navigate to="/feed" replace />} />
                    <Route path="/payments" element={<Navigate to="/obligations" replace />} />
                    <Route path="/subscriptions" element={<Navigate to="/obligations" replace />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </main>
                <TabBar />
              </div>
            </QuickExpenseProvider>
          </ToastProvider>
        </PeriodProvider>
        </SettingsLocale>
      </BrowserRouter>
    </QueryClientProvider>
  );
}